import { useLoader } from '@react-three/fiber'
import { Suspense, useRef } from 'react'
import {
  DoubleSide,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  Shape,
  SphereGeometry,
  TextureLoader,
  Vector2,
  Vector3,
} from 'three'
import { DragControls } from '~/components/DrawingCanvas/util/DragControls'
import { modelToUI, vectorUIToModel } from '~/components/DrawingCanvas/util/units'
import theme from '~/config/theme'
import { useAppDispatch } from '~/store/hooks'
import { updateObstruction } from '~/store/objects'
import { Obstruction } from '~/store/objects/types'

function ErrorIconTexture() {
  const texture = useLoader(TextureLoader, new URL(`/src/assets/textures/error.png`, import.meta.url).href)
  return (
    <>
      <meshBasicMaterial color="white" attach="material-0" />
      <meshBasicMaterial map={texture} toneMapped={false} attach="material-1" />
      <meshBasicMaterial map={texture} toneMapped={false} attach="material-2" />
    </>
  )
}

type ObstructionClearanceMeshProps = {
  isError: boolean
  isSelected: boolean
  isOnlySelected: boolean
  bounds: Vector2[]
} & Obstruction

const Z_OFFSET_TO_GIVE_CORNER_POINT_PRIORITY = 1

const CornerPoint = () => {
  const cornerRef = useRef<Mesh<SphereGeometry, MeshBasicMaterial>>(null!)
  return (
    <mesh
      ref={cornerRef}
      onPointerEnter={() => cornerRef.current.material.color.set(theme.colors.three.selectionBox)}
      onPointerLeave={() => cornerRef.current.material.color.set(theme.colors.three.valid)}
      position-z={Z_OFFSET_TO_GIVE_CORNER_POINT_PRIORITY}
    >
      <sphereGeometry />
      <meshBasicMaterial color={theme.colors.three.valid} />
    </mesh>
  )
}

export const ObstructionClearanceMesh = ({
  isSelected,
  isOnlySelected,
  isError,
  bounds,
  ...props
}: ObstructionClearanceMeshProps) => {
  const dispatch = useAppDispatch()
  const height = modelToUI(props.height)
  const isBasicObstruction = props.obstructionType === 'basic'
  const isVisible = isBasicObstruction || isSelected || isError
  const materialColor = isError ? theme.colors.three.invalid : 'grey'
  const isTransparent = !isBasicObstruction
  const opacity = isBasicObstruction ? 1 : 0.75

  const cornersRef = useRef<Group>(null)
  const meshRef = useRef<Mesh>(null!)

  return (
    <>
      <mesh rotation-x={Math.PI / 2} position-z={height} visible={isError}>
        <cylinderGeometry args={[4, 4]} />
        <Suspense fallback={<meshBasicMaterial color="white" />}>
          <ErrorIconTexture />
        </Suspense>
      </mesh>
      {!props.resizable && (
        <group ref={cornersRef} visible={isBasicObstruction && isOnlySelected}>
          {bounds.map(({ x, y }, i) => (
            <DragControls
              key={i}
              dragDisabled={!isBasicObstruction}
              onDrag={(_, delta) => {
                const newPosition = new Vector3().copy(delta).add({ x, y, z: height })
                cornersRef.current?.children[i].position.copy(newPosition)
                meshRef.current.geometry.dispose()
                const newBounds = bounds.map((position, index) =>
                  index === i ? new Vector2(newPosition.x, newPosition.y) : position
                )
                meshRef.current.geometry = new ExtrudeGeometry(new Shape(newBounds))
              }}
              onDragEnd={() => {
                const newPositions = cornersRef.current?.children.map(({ position }) =>
                  vectorUIToModel(position).add(props.position)
                )
                dispatch(
                  updateObstruction({
                    obstruction: {
                      ...props,
                      positions: newPositions,
                    },
                  })
                )
              }}
              position={[x, y, height]}
            >
              <CornerPoint />
            </DragControls>
          ))}
        </group>
      )}
      <mesh visible={isVisible} ref={meshRef}>
        <extrudeGeometry args={[new Shape(bounds), { depth: height }]} />
        <meshStandardMaterial
          color={props.color ?? materialColor}
          transparent={isTransparent}
          depthWrite={isBasicObstruction}
          opacity={opacity}
          side={DoubleSide}
        />
      </mesh>
    </>
  )
}
